import { postEvent, postPage, postAdvisorEvent, postAdvisorPage } from "../core/services/gutcheck-api.service";
import { useAuth0 } from "@auth0/auth0-react";
import Membership from "../features/users/types/Membership";

const useAnalytics = () => {

    const analytics = (window as any).analytics;
    const useClient = process.env.REACT_APP_ENABLE_SEGMENT === "true" && analytics.initialized;
    const trackingEnabled = process.env.REACT_APP_ENABLE_SEGMENT === "true";
    const { getAccessTokenSilently } = useAuth0();

    const trackPage = async (page: string, additionalData: any = {}) => {
        if (!trackingEnabled) {
            return;
        }
        if (useClient) {
            analytics.page(page, {
                ...additionalData
            });
        } else {
            // TODO: Handle guest users
            try {
                const accessToken = await getAccessTokenSilently();
                const newPage = await postPage(
                    page,
                    {
                        ...additionalData,
                        url: window.location.href,
                        path: window.location.pathname,
                        title: document.title,
                        name: page,
                        referrer: document.referrer
                    },
                    accessToken
                );
            } catch (error) {
                console.error("Error tracking page", error);
            }
        }
    };

    const trackEvent = async (event: string, additionalData: any = {}, callback: any = null) => {
        if (!trackingEnabled) {
            if (callback) {
                callback();
            }
            return;
        } else {
            if (useClient) {
                analytics.track(event, {
                    ...additionalData
                }, callback);
            } else {
                // TODO: Handle guest users
                try {
                    const accessToken = await getAccessTokenSilently();
                    const newEvent = await postEvent(
                        event,
                        {
                            ...additionalData
                        },
                        accessToken
                    );

                    if (callback) {
                        callback();
                    }
                } catch (error) {
                    console.error("Error tracking event", error);
                }
            }
        }
    }

    const trackUser = (user: any, additionalData: any = {}) => {
        if (!trackingEnabled) {
            return;
        }
        if (useClient) {
            if (!user || !user.uuid) {
                return;
            }
            analytics.identify(user.uuid, {
                ...additionalData,
                externalId: user?.external_id ?? user?.externalId,
                email: user?.email_address ?? user?.emailAddress ?? "",
                name: user.profile?.name ?? "",
                is_expert: false,
                latest_user_type: user.memberships?.find((membership: Membership) => (membership.organization && membership.role === "contributor")) ? "firm" :
                    user.memberships?.find((membership: Membership) => (membership.project && membership.role === "contributor")) ? "company" :
                    user.memberships?.find((membership: Membership) => (membership.project && membership.role === "guest")) ? "guest" : null
            });
        }
    }

    const trackAdvisorPage = async (advisor: any, page: string, additionalData: any = {}, callback: any = null) => {
        if (!trackingEnabled) {
            if (callback) {
                callback();
            }
            return;
        } else {
            if (useClient) {
                analytics.page(page, {
                    ...additionalData,
                    organization: additionalData?.organization ?? advisor?.survey_link?.project?.organization?.name ?? "",
                    project: additionalData?.project ?? advisor?.survey_link?.project?.public_title ?? ""
                });
            } else {
                const newPage = await postAdvisorPage(advisor?.uuid, page, {
                    ...additionalData,
                    organization: additionalData?.organization ?? advisor?.survey_link?.project?.organization?.name ?? "",
                    project: additionalData?.project ?? advisor?.survey_link?.project?.public_title ?? "",
                    url: window.location.href,
                    path: window.location.pathname,
                    title: document.title,
                    referrer: document.referrer
                });
                if (callback) {
                    callback();
                }
            }
        }

    }

    const trackAdvisorEvent = async (advisor: any, event: string, additionalData: any = {}, callback: any = null) => {
        if (!trackingEnabled) {
            if (callback) {
                callback();
            }
            return;
        } else {
            if (useClient) {
                analytics.track(event, {
                    ...additionalData,
                    organization: additionalData?.organization ?? advisor?.survey_link?.project?.organization?.name ?? "",
                    project: additionalData?.project ?? advisor?.survey_link?.project?.public_title ?? ""
                }, callback);
            } else {
                const newEvent = await postAdvisorEvent(advisor.uuid, event, {
                    ...additionalData,
                    organization: additionalData?.organization ?? advisor?.survey_link?.project?.organization?.name ?? "",
                    project: additionalData?.project ?? advisor?.survey_link?.project?.public_title ?? ""
                });

                if (callback) {
                    callback();
                }
            }
        }
    }

    const trackAdvisorUser = async (advisor: any, additionalData: any = {}) => {
        if (!trackingEnabled) {
            return;
        }
        const emailAddress = advisor.user?.email_address ?? advisor.email_address ?? advisor.viewpoints?.[0]?.email;
        if (useClient) {
            analytics.identify(advisor.uuid, {
                ...additionalData,
                email: emailAddress,
                is_expert: true,
                latest_user_type: "expert"
            });
        }
    }

    return { trackPage, trackEvent, trackUser, trackAdvisorPage, trackAdvisorEvent, trackAdvisorUser };


};

export default useAnalytics;